// =============================================================================
// /demo - Branded standalone video page (light warm "consulting" theme)
// =============================================================================
// The hero demo video at /videos/restack-demo.mp4 is a raw MP4 served by
// Firebase Hosting - fine for embedding inside the landing page, but
// when shared as a direct URL on LinkedIn/Twitter it shows no preview,
// no title, no branding (just the raw video).
//
// This page wraps the same video in a branded layout that matches the
// marketing site: warm ivory background, terracotta accent, Inter type.
// A small Restack mark anchors the top-left (links home); the video leads,
// with the title, three bullets, and the founder-led request form in the
// right-hand column.
//
// This is a conversion page, so it holds to one action: the request form.
// The header hides its auth actions, there is no footer, no second CTA, and
// no FAQ. Anything that gives a visitor somewhere else to click costs a
// demo request. The form validates client-side, per field, with the error
// under the field it belongs to.
//
// SEO/OG meta still gives LinkedIn the proper card preview when the URL
// is shared. Designed to be the shareable URL: re-stack.com/demo
// =============================================================================

import React, { useRef, useState, useCallback } from 'react';
import { Box, Typography, Button, useMediaQuery, useTheme } from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import SEO from '../../shared/components/SEO';
import { SiteHeader } from '../../shared/components/SiteHeader';
import { buildFunctionUrl } from '../../shared/utils';

const LINKEDIN_URL = 'https://www.linkedin.com/in/heyhiharsh/';
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Light warm palette - mirrors the marketing site (HomePage).
const INK = '#2E140A';                     // primary text + primary CTA fill
const INK_SOFT = 'rgba(46, 20, 10, 0.62)'; // secondary text (caption + link)
const ACCENT = '#B83B1D';                  // terracotta accent
const PAPER = '#FAF9F7';                   // text on the dark CTA
const SANS = "'Inter', sans-serif";

// No VideoObject schema is declared here on purpose. /demo is served from
// build/demo.html, and scripts/generate-demo-html.js already stamps a
// VideoObject into that file's head at build time. Adding a second one through
// SEO.js would put two VideoObject blocks on the same page with different
// uploadDate and duration values, which is worse for a crawler than one.

// Visually hidden but still read by screen readers. Used for the form labels,
// which the design carries as placeholders; a placeholder is not a label and
// disappears the moment someone types.
const SR_ONLY = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

// Shared formatting for the caption line and the LinkedIn link so the
// two read as the same kind of text.
const SUBTLE_TEXT = {
  fontFamily: SANS,
  fontWeight: 400,
  fontSize: { xs: '0.78rem', md: '0.8rem' },
  lineHeight: 1.45,
  letterSpacing: '-0.005em',
  color: INK_SOFT,
};

// Mask for the line texture - a scatter of soft patches rather than one
// even vignette, so the lines fade in here and there. All patches sit in
// the upper (video) region so the area below the divider stays clean.
const GRID_MASK = [
  'radial-gradient(circle 360px at 12% 16%, #000 0%, transparent 78%)',
  'radial-gradient(circle 320px at 90% 22%, #000 0%, transparent 78%)',
  'radial-gradient(circle 300px at 48% 8%, #000 0%, transparent 80%)',
  'radial-gradient(circle 300px at 26% 52%, #000 0%, transparent 76%)',
  'radial-gradient(circle 320px at 82% 56%, #000 0%, transparent 76%)',
].join(', ');

// Warm ambient glow layered over the ivory base - same recipe as the
// landing hero, so /demo reads as the same surface as re-stack.com.
const PAGE_BG = [
  'radial-gradient(ellipse 90% 50% at 50% -10%, rgba(184, 59, 29, 0.06) 0%, transparent 55%)',
  'radial-gradient(ellipse 60% 40% at 8% 0%, rgba(184, 59, 29, 0.035) 0%, transparent 45%)',
  'radial-gradient(ellipse 50% 35% at 95% 5%, rgba(212, 105, 78, 0.03) 0%, transparent 40%)',
  'linear-gradient(180deg, #fafaf9 0%, #f8f7f5 45%, #f4f3f0 100%)',
].join(', ');

// What the demo actually shows, in the order the video plays, so a viewer who
// skims (no audio, no click-play) still gets the gist.
const DEMO_BULLETS = [
  'Reads your live deck and the source material you point it at',
  'Drafts new slides with cited research, in place',
  'Matches your existing layout, fonts, and brand',
];

/**
 * Client-side validation for the demo request. Returns a map of field name to
 * a specific message. Specific beats polite: "Add your work email" tells the
 * person what to do, "Please fill in the form" does not.
 */
function validateDemoRequest({ name, email }) {
  const errors = {};
  if (!name) {
    errors.name = 'Add your name so I know who I am replying to.';
  }
  if (!email) {
    errors.email = 'Add your work email so I can reply.';
  } else if (!EMAIL_RE.test(email)) {
    errors.email = 'That does not look like an email address. Check for a typo.';
  }
  return errors;
}

export default function DemoPage() {
  const theme = useTheme();
  // Single-column (mobile/tablet) view. Below md the page stacks and we switch
  // the video to native controls - programmatic play()/custom overlay is
  // unreliable on mobile browsers - and lead with the title above the video.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const videoRef = useRef(null);
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const handlePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.play()?.catch(() => {});
    setPlaying(true);
  }, []);

  // Demo-request form. Captures an email and pings the founder (reuses the
  // sendFeedback endpoint, which emails the team). Founder-led, push motion:
  // a short form > a self-serve calendar link.
  const [form, setForm] = useState({ name: '', email: '', linkedin: '' });
  const [status, setStatus] = useState('idle'); // idle | submitting | sent | error
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const onField = (key) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [key]: value }));
    // Clear a field's error the moment the person starts fixing it, rather
    // than making them submit again to find out.
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const name = form.name.trim();
      const email = form.email.trim();
      const linkedin = form.linkedin.trim();

      const errors = validateDemoRequest({ name, email });
      if (Object.keys(errors).length) {
        setFieldErrors(errors);
        setSubmitError('');
        setStatus('idle');
        // Move focus to the first field that needs fixing, so the message is
        // announced and the caret is already where the work is.
        (errors.name ? nameRef.current : emailRef.current)?.focus();
        return;
      }

      setFieldErrors({});
      setSubmitError('');
      setStatus('submitting');
      try {
        const res = await fetch(buildFunctionUrl('sendFeedback', { forceProduction: true }), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            feedbackType: 'Demo request',
            userName: name,
            userEmail: email,
            feedback: `New demo request from /demo.\n\nName: ${name}\nWork email: ${email}\nLinkedIn: ${linkedin || 'not provided'}`,
          }),
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        setStatus('sent');
      } catch (err) {
        setSubmitError('That did not send. Email hi@re-stack.com and I will pick it up there.');
        setStatus('error');
      }
    },
    [form],
  );

  const firstName = form.name.trim().split(' ')[0];

  const fieldSx = (hasError) => ({
    width: '100%',
    boxSizing: 'border-box',
    // Safari/iOS ignore border-radius on native inputs unless appearance is
    // reset, which makes the fields render with a different corner shape than
    // the button even though computed border-radius reads 14px. Reset it so
    // the 14px radius is honored consistently across browsers.
    appearance: 'none',
    WebkitAppearance: 'none',
    fontFamily: SANS,
    fontSize: '0.82rem',
    color: INK,
    px: 1.25,
    py: 0.9,
    borderRadius: '12px',
    border: `1px solid ${hasError ? ACCENT : 'rgba(46, 20, 10, 0.18)'}`,
    backgroundColor: '#fff',
    outline: 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    '&::placeholder': { color: 'rgba(46, 20, 10, 0.4)' },
    '&:hover': { borderColor: hasError ? ACCENT : 'rgba(46, 20, 10, 0.3)' },
    '&:focus': { borderColor: ACCENT, boxShadow: '0 0 0 3px rgba(184, 59, 29, 0.12)' },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  });

  const errorTextSx = {
    fontFamily: SANS,
    fontSize: '0.72rem',
    lineHeight: 1.4,
    color: ACCENT,
    m: 0,
  };

  return (
    <>
      <SEO
        title="Restack Demo | AI for Private Equity"
        description="See Restack in action: the deliverable engine for Private Equity, working inside your live Office files alongside your team. 1 minute."
        canonical="/demo"
        ogType="video.other"
        ogImage="/og-image-demo.png"
      />

      {/* Shared marketing header - matches the landing page (fixed). On the
          demo page the Login + Request a Demo actions are hidden so the page
          stays focused on the walkthrough and the one form. */}
      <SiteHeader hideAuthActions />

      <Box
        sx={{
          position: 'relative',
          // Own stacking context so the z-index:-1 grid pseudo stays
          // above this Box's background but below the content.
          isolation: 'isolate',
          // Designed to fit one viewport at desktop: height locked to
          // 100vh, overflow hidden so there is no scrollbar and nothing
          // hangs off the bottom. On mobile we allow scroll because
          // stacked content cannot fit in 812px. The global stylesheet
          // locks #root / .App to overflow:hidden for the Office add-in
          // case; we set our own height here so that still works.
          // The form makes the right column taller than one screen, so the
          // page scrolls within the viewport-height box instead of clipping.
          // Mobile browsers shrink the visual viewport when the address bar is
          // visible; a fixed 100vh overshoots that and nested 100vh scroll is
          // flaky on iOS, which made the page feel "stuck" on the first fold.
          // 100dvh tracks the actual visible height and momentum scrolling
          // makes the inner scroll behave like the page.
          height: { xs: '100dvh', md: '100vh' },
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          width: '100%',
          background: PAGE_BG,
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 'calc(56px + 0.5rem)', md: '64px' },
          pb: { xs: 2, md: 2.25 },
          // Grid texture - same faint 56px grid as the landing-page hero
          // (.landing-hero::after). Revealed only through the scattered
          // patches in GRID_MASK, all of which sit in the upper video
          // region, so the page stays clean below the divider.
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'linear-gradient(to right, rgba(30, 22, 18, 0.055) 1px, transparent 1px), linear-gradient(to bottom, rgba(30, 22, 18, 0.055) 1px, transparent 1px)',
            backgroundSize: '56px 56px',
            WebkitMaskImage: GRID_MASK,
            maskImage: GRID_MASK,
            pointerEvents: 'none',
            zIndex: -1,
          },
        }}
      >
        {/* Decorative flowing lines - the same faint terracotta bezier
            curves as the landing-page hero (landing-hero__deco-svg), to
            give the background a little movement behind the video. */}
        <Box
          component="svg"
          aria-hidden="true"
          focusable="false"
          viewBox="0 0 1440 900"
          preserveAspectRatio="none"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: -1,
          }}
        >
          {/* Curves reposition: one sweeps from the upper-left corner
              across the top of the video and rises into the right-column
              CTA card; the other sweeps from below the video toward the
              same destination. Visually connects "watch" → "book". */}
          <path
            d="M-120 180 C220 140, 540 220, 820 240 S1180 200, 1380 280"
            stroke="rgba(184, 59, 29, 0.07)"
            strokeWidth="1.5"
            fill="none"
          />
          <path
            d="M-80 620 C260 580, 580 660, 880 600 S1180 460, 1380 380"
            stroke="rgba(184, 59, 29, 0.045)"
            strokeWidth="1.2"
            fill="none"
          />
          <circle cx="820" cy="242" r="3.5" fill="rgba(184, 59, 29, 0.07)" />
          <circle cx="280" cy="160" r="3" fill="rgba(184, 59, 29, 0.05)" />
          <circle cx="880" cy="602" r="3.5" fill="rgba(184, 59, 29, 0.05)" />
          <circle cx="1380" cy="378" r="4" fill="rgba(184, 59, 29, 0.09)" />
        </Box>

        {/* MAIN LAYOUT - video LEFT at 75%, info RIGHT at 25%. On mobile it
            stacks single-column: title, video, info.
            The three blocks are placed by explicit grid coordinates rather
            than rendered twice at two breakpoints, so the page ships exactly
            one <h1> instead of a visible one plus a display:none duplicate,
            and the DOM order (title, video, details) is also the correct
            reading order. alignItems:stretch lets the video span both rows on
            desktop while the info column fills the second row, so the founder
            card can still anchor to the bottom edge via mt:auto. */}
        <Box
          component="main"
          id="main-content"
          sx={{
            flexShrink: 0,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'minmax(0, 3fr) minmax(0, 1fr)',
            },
            gridTemplateRows: { md: 'auto 1fr' },
            columnGap: { md: 3 },
            rowGap: { xs: 2.5, md: 1.75 },
            alignItems: { xs: 'start', md: 'stretch' },
          }}
        >
          {/* Title block. Row 1 of the right column on desktop, first on
              mobile. pt on md+ aligns it with the video's actual slide content
              area (the slide starts ~20px below the video frame top); px lines
              it up with the card's content edges. */}
          <Box
            sx={{
              gridColumn: { xs: '1', md: '2' },
              gridRow: { xs: '1', md: '1' },
              pt: { xs: 0, md: 2.5 },
              px: { md: 1.75 },
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontFamily: SANS,
                fontSize: { xs: '1.35rem', md: '1.25rem' },
                fontWeight: 700,
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                color: INK,
                textWrap: 'balance',
              }}
            >
              See Restack build cited slides in your live deck
            </Typography>
            <Typography
              sx={{
                fontFamily: SANS,
                fontSize: { xs: '0.92rem', md: '0.88rem' },
                fontWeight: 400,
                lineHeight: 1.5,
                letterSpacing: '-0.005em',
                color: INK_SOFT,
                mt: 0.6,
              }}
            >
              One AI agent that works inside your live PowerPoint, Excel, and Word, built for private equity teams.
            </Typography>
          </Box>

          {/* Video. Spans both rows on desktop so it holds the left column. */}
          <Box
            sx={{
              gridColumn: { xs: '1', md: '1' },
              gridRow: { xs: '2', md: '1 / 3' },
              position: 'relative',
              width: '100%',
              aspectRatio: '3566 / 2324',
              borderRadius: '20px',
              overflow: 'hidden',
              // Light backdrop (not #000) so no dark fringe shows at the
              // rounded corners where the clip meets the border.
              background: '#E8E5E1',
              boxShadow:
                '0 32px 80px rgba(46, 20, 10, 0.18), 0 12px 32px rgba(46, 20, 10, 0.12), 0 0 0 1px rgba(46, 20, 10, 0.06)',
              '&:hover .demo-page-play-button': playing
                ? {}
                : {
                    transform: 'translate(-50%, -50%) scale(1.06)',
                    background: ACCENT,
                  },
            }}
          >
            <Box
              component="video"
              ref={videoRef}
              src="/videos/restack-demo.mp4"
              preload="metadata"
              playsInline
              aria-label="Restack demo walkthrough, one minute"
              // Mobile: always show native controls - programmatic play() with a
              // custom overlay is unreliable across mobile browsers (the "weird
              // play" state). Desktop: keep the designed custom play button until
              // the user starts playback.
              controls={playing || isMobile}
              onPlay={() => setPlaying(true)}
              onClick={(playing || isMobile) ? undefined : handlePlay}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
                borderRadius: '20px',
                cursor: (playing || isMobile) ? 'default' : 'pointer',
                background: '#E8E5E1',
              }}
            />

            {/* Inset warm frame - sits on top of the video's edges to
                mask the thin black edge lines and keep the border even.
                pointer-events: none so it never blocks the video. */}
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '20px',
                border: '1px solid #E8E5E1',
                pointerEvents: 'none',
              }}
            />

            {!playing && !isMobile && (
              <Box
                component="button"
                type="button"
                className="demo-page-play-button"
                onClick={handlePlay}
                aria-label="Play Restack demo video"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 84,
                  height: 84,
                  borderRadius: '50%',
                  background: 'rgba(184, 59, 29, 0.92)',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow:
                    '0 12px 32px rgba(0, 0, 0, 0.45), 0 0 0 6px rgba(255, 255, 255, 0.14)',
                  transition:
                    'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), background 0.2s ease',
                  padding: 0,
                  '&:active': { transform: 'translate(-50%, -50%) scale(0.97)' },
                  '&:focus-visible': {
                    outline: '3px solid #ffffff',
                    outlineOffset: 4,
                  },
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                }}
              >
                <Box
                  aria-hidden="true"
                  component="span"
                  sx={{
                    width: 0,
                    height: 0,
                    borderTop: '14px solid transparent',
                    borderBottom: '14px solid transparent',
                    borderLeft: '22px solid #ffffff',
                    marginLeft: '5px',
                  }}
                />
              </Box>
            )}

            {/* Caption / disclosure pill - bottom-right, shown at all
                times. Before playback it invites the watch; during
                playback it discloses that the recording is sped up.
                Right side keeps it clear of the native playback controls
                (play button / timer sit bottom-left). */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                padding: '0.4rem 0.8rem',
                borderRadius: '12px',
                background: 'rgba(0, 0, 0, 0.55)',
                backdropFilter: 'blur(8px)',
                fontFamily: SANS,
                fontSize: '0.75rem',
                fontWeight: 400,
                letterSpacing: '-0.005em',
                color: '#ffffff',
                pointerEvents: 'none',
              }}
            >
              <Box
                aria-hidden="true"
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: ACCENT,
                }}
              />
              {playing ? 'Sped up' : '1-min walkthrough'}
            </Box>
          </Box>

          {/* Info column - bullets, then the founder card anchored to the
              bottom edge via mt:auto so the column balances against the
              video on the left. */}
          <Box
            sx={{
              gridColumn: { xs: '1', md: '2' },
              gridRow: { xs: '3', md: '2' },
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 2, md: 1.75 },
              height: '100%',
            }}
          >
            <Box
              component="ul"
              sx={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.85,
                // Inset to line up with the form card's content edges.
                px: { xs: 2, md: 1.75 },
              }}
            >
              {DEMO_BULLETS.map((item) => (
                <Box
                  key={item}
                  component="li"
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    fontFamily: SANS,
                    fontSize: { xs: '0.85rem', md: '0.82rem' },
                    fontWeight: 500,
                    lineHeight: 1.45,
                    letterSpacing: '-0.005em',
                    color: INK,
                  }}
                >
                  <Box
                    aria-hidden="true"
                    sx={{
                      flexShrink: 0,
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      background: ACCENT,
                      mt: '0.55em',
                    }}
                  />
                  <Box component="span">{item}</Box>
                </Box>
              ))}
            </Box>

            {/* Founder + demo-request form - one card. The founder profile
                (with the LinkedIn logo) sits on top; the form follows below.
                Anchored toward the bottom so it balances against the video. */}
            <Box
              sx={{
                mt: { xs: 1, md: 'auto' },
                width: '100%',
                borderRadius: '16px',
                border: '1px solid rgba(46, 20, 10, 0.1)',
                background: 'rgba(255, 255, 255, 0.55)',
                p: { xs: 2, md: 1.75 },
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 1.5, md: 1.25 },
                textAlign: 'left',
              }}
            >
              {/* Founder profile - avatar + pedigree, LinkedIn logo alongside */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                  component="img"
                  src="/founder/harsh.jpg"
                  alt="Harsh, founder of Restack"
                  width={44}
                  height={44}
                  sx={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, background: '#E8E5E1' }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                  <Box component="span" sx={{ fontFamily: SANS, fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.2, letterSpacing: '-0.01em', color: INK }}>
                    Harsh
                  </Box>
                  <Box component="span" sx={{ fontFamily: SANS, fontSize: '0.72rem', fontWeight: 400, letterSpacing: '-0.005em', color: INK_SOFT, mt: 0.2, lineHeight: 1.3 }}>
                    Founder, Restack &middot; ex-Roland Berger, product lead at an $8B PE firm
                  </Box>
                  <Box component="span" sx={{ fontFamily: SANS, fontSize: '0.72rem', fontWeight: 500, letterSpacing: '-0.005em', color: INK, mt: 0.4, lineHeight: 1.3 }}>
                    You&rsquo;ll hear from Harsh personally.
                  </Box>
                </Box>
                <Box
                  component="a"
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Harsh on LinkedIn, opens in a new tab"
                  sx={{
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '12px',
                    color: INK_SOFT,
                    transition: 'color 0.18s ease, background-color 0.18s ease, transform 0.12s ease',
                    '&:hover': { color: '#0A66C2', backgroundColor: 'rgba(10, 102, 194, 0.08)' },
                    '&:active': { transform: 'translateY(1px)' },
                    '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  }}
                >
                  <LinkedInIcon aria-hidden="true" sx={{ fontSize: 22 }} />
                </Box>
              </Box>

              {/* Demo-request form (or success state) */}
              {status === 'sent' ? (
                <Box role="status" sx={{ borderRadius: '16px', border: '1px solid rgba(27, 122, 79, 0.3)', background: 'rgba(27, 122, 79, 0.06)', p: 1.5 }}>
                  <Box component="div" sx={{ fontFamily: SANS, fontWeight: 600, fontSize: '0.85rem', color: INK, mb: 0.3 }}>
                    Thanks{firstName ? `, ${firstName}` : ''}.
                  </Box>
                  <Box component="div" sx={{ fontFamily: SANS, fontSize: '0.8rem', lineHeight: 1.45, color: INK_SOFT }}>
                    I&rsquo;ll personally reach out within a day to set up a time.
                  </Box>
                </Box>
              ) : (
                <>
                  <Box component="div" sx={{ ...SUBTLE_TEXT, fontSize: { xs: '0.8rem', md: '0.78rem' } }}>
                    Drop your details and I&rsquo;ll personally reach out within a day to set up a time.
                  </Box>
                  {/* noValidate so our own per-field messages show instead of
                      the browser's generic bubble, which is not stylable and
                      disappears on the next click. */}
                  <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Box>
                      <Box component="label" htmlFor="demo-name" sx={SR_ONLY}>Your name</Box>
                      <Box
                        component="input"
                        id="demo-name"
                        ref={nameRef}
                        type="text"
                        name="name"
                        autoComplete="name"
                        placeholder="Your name"
                        value={form.name}
                        onChange={onField('name')}
                        aria-invalid={fieldErrors.name ? 'true' : undefined}
                        aria-describedby={fieldErrors.name ? 'demo-name-error' : undefined}
                        sx={fieldSx(Boolean(fieldErrors.name))}
                      />
                      {fieldErrors.name && (
                        <Box component="p" id="demo-name-error" sx={{ ...errorTextSx, mt: 0.4 }}>
                          {fieldErrors.name}
                        </Box>
                      )}
                    </Box>

                    <Box>
                      <Box component="label" htmlFor="demo-email" sx={SR_ONLY}>Work email</Box>
                      <Box
                        component="input"
                        id="demo-email"
                        ref={emailRef}
                        type="email"
                        name="email"
                        autoComplete="email"
                        placeholder="Work email"
                        value={form.email}
                        onChange={onField('email')}
                        aria-invalid={fieldErrors.email ? 'true' : undefined}
                        aria-describedby={fieldErrors.email ? 'demo-email-error' : undefined}
                        sx={fieldSx(Boolean(fieldErrors.email))}
                      />
                      {fieldErrors.email && (
                        <Box component="p" id="demo-email-error" sx={{ ...errorTextSx, mt: 0.4 }}>
                          {fieldErrors.email}
                        </Box>
                      )}
                    </Box>

                    <Box>
                      <Box component="label" htmlFor="demo-linkedin" sx={SR_ONLY}>LinkedIn profile, optional</Box>
                      <Box
                        component="input"
                        id="demo-linkedin"
                        type="text"
                        name="linkedin"
                        placeholder="LinkedIn (optional)"
                        value={form.linkedin}
                        onChange={onField('linkedin')}
                        sx={fieldSx(false)}
                      />
                    </Box>

                    {submitError && (
                      <Box component="p" role="alert" sx={errorTextSx}>
                        {submitError}
                      </Box>
                    )}

                    <Button
                      type="submit"
                      disabled={status === 'submitting'}
                      sx={{
                        borderRadius: '999px', height: 38, minHeight: 38, px: 2, py: 0, mt: 0.25,
                        fontFamily: SANS, fontWeight: 500, fontSize: '0.82rem', letterSpacing: '-0.005em', lineHeight: 1,
                        textTransform: 'none', color: PAPER, backgroundColor: INK,
                        boxShadow: '0 4px 12px rgba(46, 20, 10, 0.18)', width: '100%',
                        transition: 'transform 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease',
                        '&:hover': { backgroundColor: '#1A0A04', boxShadow: '0 6px 16px rgba(46, 20, 10, 0.28)', transform: 'translateY(-1px)' },
                        '&:active': { transform: 'translateY(1px)', boxShadow: '0 2px 6px rgba(46, 20, 10, 0.2)' },
                        '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
                        '&.Mui-disabled': { color: 'rgba(250, 249, 247, 0.7)', backgroundColor: 'rgba(46, 20, 10, 0.55)' },
                        '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
                      }}
                    >
                      {status === 'submitting' ? 'Sending…' : 'See it on your own deck'}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
